/**
 * MOTE-123 — option catalogs + helpers shared by `<RecurrenceEditor>` and
 * the BoardCard recurrence chip. Mirrors the legacy `Recurring.utils.js`
 * (`3mote-fe/src/components/Recurring/Recurring.utils.js`); the labelKey
 * fields are i18n keys under the `board` namespace rather than baked-in
 * English so the picker copy localizes.
 */

import type {
  RecurrenceFrequency,
  RecurrenceInput,
  RecurrenceOrdinal,
  RecurrenceTriggerType,
  RecurrenceWeekday,
} from '@/api/types-bridge'

export interface RecurrenceFormState {
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek: RecurrenceWeekday[]
  monthlyMode: 'day_of_month' | 'day_of_week'
  dateOfMonth: number | null
  /** "first" / "second" / … / "last". Wire name: `dayOfMonth`. */
  monthlyOrdinal: RecurrenceOrdinal
  /** "monday" … "sunday". Wire name: `dayInMonth`. */
  monthlyWeekday: RecurrenceWeekday
  endType: 'after' | 'on' | 'never'
  occurrences: number | null
  endDate: string | null
  triggerType: RecurrenceTriggerType
}

export const DEFAULT_RECURRENCE_STATE: RecurrenceFormState = {
  frequency: 'weekly',
  interval: 1,
  daysOfWeek: ['monday'],
  monthlyMode: 'day_of_month',
  dateOfMonth: 1,
  monthlyOrdinal: 'first',
  monthlyWeekday: 'monday',
  endType: 'never',
  occurrences: null,
  endDate: null,
  triggerType: 'on_completion',
}

export interface RecurrenceOption<T> {
  value: T
  labelKey: string
}

export const FREQUENCY_OPTIONS: ReadonlyArray<RecurrenceOption<RecurrenceFrequency>> = [
  { value: 'daily', labelKey: 'recurrence.frequency.daily' },
  { value: 'weekly', labelKey: 'recurrence.frequency.weekly' },
  { value: 'monthly', labelKey: 'recurrence.frequency.monthly' },
  { value: 'yearly', labelKey: 'recurrence.frequency.yearly' },
]

export const WEEKDAY_OPTIONS: ReadonlyArray<RecurrenceOption<RecurrenceWeekday>> = [
  { value: 'monday', labelKey: 'recurrence.weekday.monday' },
  { value: 'tuesday', labelKey: 'recurrence.weekday.tuesday' },
  { value: 'wednesday', labelKey: 'recurrence.weekday.wednesday' },
  { value: 'thursday', labelKey: 'recurrence.weekday.thursday' },
  { value: 'friday', labelKey: 'recurrence.weekday.friday' },
  { value: 'saturday', labelKey: 'recurrence.weekday.saturday' },
  { value: 'sunday', labelKey: 'recurrence.weekday.sunday' },
]

export const ORDINAL_OPTIONS: ReadonlyArray<RecurrenceOption<RecurrenceOrdinal>> = [
  { value: 'first', labelKey: 'recurrence.ordinal.first' },
  { value: 'second', labelKey: 'recurrence.ordinal.second' },
  { value: 'third', labelKey: 'recurrence.ordinal.third' },
  { value: 'fourth', labelKey: 'recurrence.ordinal.fourth' },
  { value: 'last', labelKey: 'recurrence.ordinal.last' },
]

/**
 * 1–30 numeric + `31 → "Last"`. Mirrors the legacy `dateOfMonthOptions`:
 * the value `31` means "the last day of the month" (the BE clamps to the
 * actual last day per month per `advanceMonthlyByDate`).
 */
export const DATE_OF_MONTH_OPTIONS: ReadonlyArray<{ value: number; labelKey: string }> = [
  ...Array.from({ length: 30 }, (_, i) => {
    const n = i + 1
    return { value: n, labelKey: `recurrence.dateOfMonth.${n}` }
  }),
  { value: 31, labelKey: 'recurrence.dateOfMonth.last' },
]

export const END_TYPE_OPTIONS: ReadonlyArray<RecurrenceOption<'after' | 'on' | 'never'>> = [
  { value: 'after', labelKey: 'recurrence.endType.after' },
  { value: 'on', labelKey: 'recurrence.endType.on' },
  { value: 'never', labelKey: 'recurrence.endType.never' },
]

/**
 * Structural superset of the write shape (`RecurrenceInput`) and the read
 * shape (`BoardCard.recurrence`, whose list/monthly fields are nullable).
 * Wire names: `dayInMonth` is the WEEKDAY and `dayOfMonth` the ORDINAL.
 */
export interface RecurrenceRuleLike {
  frequency: RecurrenceFrequency
  interval: number
  triggerType: RecurrenceTriggerType
  daysOfWeek?: readonly RecurrenceWeekday[] | null
  dateOfMonth?: number | null
  dayInMonth?: RecurrenceWeekday | null
  dayOfMonth?: RecurrenceOrdinal | null
  endDate?: string | null
  occurrences?: number | null
}

/**
 * Map a stored/wire rule → form state. Used to seed the editor from an
 * existing rule. Pre-populates reasonable monthly defaults so the user can
 * switch modes without losing draft values.
 */
export function recurrenceToFormState(rule: RecurrenceRuleLike | null): RecurrenceFormState {
  if (!rule) return { ...DEFAULT_RECURRENCE_STATE }
  const monthlyMode: RecurrenceFormState['monthlyMode'] =
    rule.frequency === 'monthly' && rule.dateOfMonth == null && rule.dayOfMonth != null
      ? 'day_of_week'
      : 'day_of_month'
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    daysOfWeek: rule.daysOfWeek && rule.daysOfWeek.length > 0 ? [...rule.daysOfWeek] : ['monday'],
    monthlyMode,
    dateOfMonth: rule.dateOfMonth ?? 1,
    monthlyOrdinal: rule.dayOfMonth ?? 'first',
    monthlyWeekday: rule.dayInMonth ?? 'monday',
    endType: rule.endDate ? 'on' : rule.occurrences != null ? 'after' : 'never',
    occurrences: rule.occurrences ?? null,
    endDate: rule.endDate ?? null,
    triggerType: rule.triggerType,
  }
}

/**
 * Map form state → `RecurrenceInput` (BE wire shape). Strips per-mode
 * fields that don't apply so the BE Zod schema doesn't complain about
 * incompatible combinations (e.g. weekly + dateOfMonth).
 */
export function formStateToRecurrence(state: RecurrenceFormState): RecurrenceInput {
  const base: RecurrenceInput = {
    frequency: state.frequency,
    interval: Math.max(1, Math.floor(state.interval || 1)),
    triggerType: state.triggerType,
  }

  if (state.frequency === 'weekly') {
    base.daysOfWeek = state.daysOfWeek.length > 0 ? state.daysOfWeek : ['monday']
  } else if (state.frequency === 'monthly') {
    if (state.monthlyMode === 'day_of_month') {
      base.dateOfMonth = state.dateOfMonth ?? 1
    } else {
      base.dayOfMonth = state.monthlyOrdinal
      base.dayInMonth = state.monthlyWeekday
    }
  }

  if (state.endType === 'after' && state.occurrences != null) {
    base.occurrences = Math.max(1, Math.floor(state.occurrences))
  } else if (state.endType === 'on' && state.endDate) {
    base.endDate = state.endDate
  }

  return base
}

/**
 * Strongly-typed i18n keys for recurrence validation errors so callers
 * can pass the returned value through `t()` without losing type safety.
 */
export type RecurrenceErrorKey =
  | 'recurrence.error.intervalRequired'
  | 'recurrence.error.weeklyDaysRequired'
  | 'recurrence.error.monthlyDateRequired'
  | 'recurrence.error.occurrencesRequired'
  | 'recurrence.error.endDateRequired'
  | 'recurrence.error.saveFailed'

/**
 * Form-level validation. Returns null when the state can be submitted,
 * else an i18n key under `board:recurrence.error.*` for the first
 * problem. Mirrors the BE Zod refinements in `writeRecurrenceForDeliverable`.
 */
export function validateRecurrenceState(state: RecurrenceFormState): RecurrenceErrorKey | null {
  if (!Number.isFinite(state.interval) || state.interval < 1) {
    return 'recurrence.error.intervalRequired'
  }
  if (state.frequency === 'weekly' && state.daysOfWeek.length === 0) {
    return 'recurrence.error.weeklyDaysRequired'
  }
  if (state.frequency === 'monthly') {
    if (state.monthlyMode === 'day_of_month' && state.dateOfMonth == null) {
      return 'recurrence.error.monthlyDateRequired'
    }
  }
  if (state.endType === 'after' && (state.occurrences == null || state.occurrences < 1)) {
    return 'recurrence.error.occurrencesRequired'
  }
  if (state.endType === 'on' && !state.endDate) {
    return 'recurrence.error.endDateRequired'
  }
  return null
}
